// Spotify OAuth with PKCE flow
// Based on https://developer.spotify.com/documentation/web-api/tutorials/code-pkce-flow

const SPOTIFY_AUTH_URL = 'https://accounts.spotify.com/authorize'
const SPOTIFY_TOKEN_URL = 'https://accounts.spotify.com/api/token'
const SPOTIFY_API_BASE = 'https://api.spotify.com/v1'
const SPOTIFY_SCOPES = 'playlist-read-private playlist-read-collaborative user-library-read'

// Storage keys
const STORAGE_KEYS = {
  accessToken: 'spotify_access_token',
  refreshToken: 'spotify_refresh_token',
  tokenExpiry: 'spotify_token_expiry',
  codeVerifier: 'spotify_code_verifier',
  clientId: 'spotify_client_id',
}

// Generate code verifier for PKCE flow
function generateCodeVerifier(): string {
  const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  const randomValues = crypto.getRandomValues(new Uint8Array(64))
  return Array.from(randomValues)
    .map((value) => possible[value % possible.length])
    .join('')
}

// Generate code challenge from verifier
async function generateCodeChallenge(codeVerifier: string): Promise<string> {
  const encoder = new TextEncoder()
  const data = encoder.encode(codeVerifier)
  const hashed = await crypto.subtle.digest('SHA-256', data)
  return base64urlencode(hashed)
}

// Encode array buffer to base64 URL
function base64urlencode(arrayBuffer: ArrayBuffer): string {
  const bytes = new Uint8Array(arrayBuffer)
  let str = ''
  bytes.forEach((byte) => {
    str += String.fromCharCode(byte)
  })
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

// Get redirect URI for OAuth callback
function getRedirectUri(): string {
  return `${window.location.origin}/spotify-callback`
}

// Access token management
export function loadSpotifyAccessToken(): string | null {
  const token = localStorage.getItem(STORAGE_KEYS.accessToken)
  const expiry = localStorage.getItem(STORAGE_KEYS.tokenExpiry)

  if (token && expiry) {
    const expiryTime = parseInt(expiry, 10)
    if (Date.now() < expiryTime) {
      return token
    }
    // Token expired, clear it
    clearSpotifyAuth()
  }
  return null
}

export function saveSpotifyAccessToken(token: string, expiresIn: number): void {
  localStorage.setItem(STORAGE_KEYS.accessToken, token)
  localStorage.setItem(STORAGE_KEYS.tokenExpiry, (Date.now() + expiresIn * 1000).toString())
}

export function clearSpotifyAuth(): void {
  localStorage.removeItem(STORAGE_KEYS.accessToken)
  localStorage.removeItem(STORAGE_KEYS.refreshToken)
  localStorage.removeItem(STORAGE_KEYS.tokenExpiry)
  localStorage.removeItem(STORAGE_KEYS.codeVerifier)
}

export function getSpotifyClientId(): string | null {
  return localStorage.getItem(STORAGE_KEYS.clientId)
}

export function setSpotifyClientId(clientId: string): void {
  localStorage.setItem(STORAGE_KEYS.clientId, clientId)
}

// Initiate Spotify OAuth authorization flow
export async function initiateSpotifyAuth(clientId: string): Promise<void> {
  // Save client ID for token exchange
  setSpotifyClientId(clientId)

  // Generate and store PKCE code verifier
  const codeVerifier = generateCodeVerifier()
  const codeChallenge = await generateCodeChallenge(codeVerifier)
  localStorage.setItem(STORAGE_KEYS.codeVerifier, codeVerifier)

  // Construct authorization URL
  const authUrl = new URL(SPOTIFY_AUTH_URL)
  const params = {
    response_type: 'code',
    client_id: clientId,
    scope: SPOTIFY_SCOPES,
    code_challenge_method: 'S256',
    code_challenge: codeChallenge,
    redirect_uri: getRedirectUri(),
  }

  authUrl.search = new URLSearchParams(params).toString()

  // Redirect to Spotify authorization page
  window.location.href = authUrl.toString()
}

// Exchange authorization code for access token
export async function exchangeCodeForToken(code: string): Promise<string> {
  const redirectUri = getRedirectUri()
  const codeVerifier = localStorage.getItem(STORAGE_KEYS.codeVerifier)
  const clientId = getSpotifyClientId()

  if (!codeVerifier) {
    throw new Error('Code verifier not found')
  }
  if (!clientId) {
    throw new Error('Client ID not found')
  }

  const response = await fetch(SPOTIFY_TOKEN_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: new URLSearchParams({
      client_id: clientId,
      grant_type: 'authorization_code',
      code: code,
      redirect_uri: redirectUri,
      code_verifier: codeVerifier,
    }),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error_description || 'Failed to exchange code for token')
  }

  const data = await response.json()

  // Save tokens
  saveSpotifyAccessToken(data.access_token, data.expires_in)
  if (data.refresh_token) {
    localStorage.setItem(STORAGE_KEYS.refreshToken, data.refresh_token)
  }

  // Clean up code verifier
  localStorage.removeItem(STORAGE_KEYS.codeVerifier)

  return data.access_token
}

// Spotify API helpers
export interface SpotifyUser {
  id: string
  display_name: string
  images: Array<{ url: string }>
}

export interface SpotifyPlaylist {
  id: string
  name: string
  description: string | null
  public: boolean
  collaborative: boolean
  owner: {
    id: string
    display_name: string
  }
  tracks: {
    total: number
    href: string
  }
  images: Array<{ url: string }>
}

export interface SpotifyTrack {
  id: string
  name: string
  uri: string
  duration_ms: number
  artists: Array<{ id: string; name: string }>
  album: {
    id: string
    name: string
    release_date: string
    images: Array<{ url: string }>
    artists: Array<{ id: string; name: string }>
  }
}

export interface SpotifyPlaylistTrack {
  added_at: string
  track: SpotifyTrack | null
}

async function spotifyApiCall<T>(endpoint: string, accessToken: string): Promise<T> {
  const response = await fetch(`${SPOTIFY_API_BASE}${endpoint}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!response.ok) {
    if (response.status === 401) {
      clearSpotifyAuth()
      throw new Error('Spotify session expired')
    }
    throw new Error(`Spotify API error: ${response.status}`)
  }

  return response.json()
}

export async function getSpotifyUser(accessToken: string): Promise<SpotifyUser> {
  return spotifyApiCall('/me', accessToken)
}

export async function getSpotifyPlaylists(
  accessToken: string,
  limit = 50,
  offset = 0
): Promise<{ items: SpotifyPlaylist[]; total: number }> {
  return spotifyApiCall(`/me/playlists?limit=${limit}&offset=${offset}`, accessToken)
}

export async function getAllSpotifyPlaylists(accessToken: string): Promise<SpotifyPlaylist[]> {
  const playlists: SpotifyPlaylist[] = []
  let offset = 0
  const limit = 50

  while (true) {
    const response = await getSpotifyPlaylists(accessToken, limit, offset)
    playlists.push(...response.items)

    if (playlists.length >= response.total) {
      break
    }
    offset += limit
  }

  return playlists
}

export async function getSpotifyLikedSongs(
  accessToken: string,
  limit = 50,
  offset = 0
): Promise<{ items: SpotifyPlaylistTrack[]; total: number }> {
  return spotifyApiCall(`/me/tracks?limit=${limit}&offset=${offset}`, accessToken)
}

export async function getPlaylistTracks(
  accessToken: string,
  playlistId: string,
  limit = 100,
  offset = 0
): Promise<{ items: SpotifyPlaylistTrack[]; total: number }> {
  return spotifyApiCall(
    `/playlists/${playlistId}/tracks?limit=${limit}&offset=${offset}`,
    accessToken
  )
}

export async function getAllPlaylistTracks(
  accessToken: string,
  playlistId: string
): Promise<SpotifyPlaylistTrack[]> {
  const tracks: SpotifyPlaylistTrack[] = []
  let offset = 0
  const limit = 100

  while (true) {
    const response = await getPlaylistTracks(accessToken, playlistId, limit, offset)
    tracks.push(...response.items)

    if (tracks.length >= response.total) {
      break
    }
    offset += limit
  }

  return tracks
}

export async function getAllLikedSongs(accessToken: string): Promise<SpotifyPlaylistTrack[]> {
  const tracks: SpotifyPlaylistTrack[] = []
  let offset = 0
  const limit = 50

  while (true) {
    const response = await getSpotifyLikedSongs(accessToken, limit, offset)
    tracks.push(...response.items)

    if (tracks.length >= response.total) {
      break
    }
    offset += limit
  }

  return tracks
}
